import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { useState, useRef, useEffect } from 'react';
import BabySeatPriceBreakdown from './babySeatPriceBreakdown';
import PopoverComponent from '../../../commons/PopoverComponent';
import { formatCurrencyWithSymbol } from '../../../../utils/formatCurrencyWithSymbol';
import { useResponsiveScreen } from '../../../../hooks/useResponsiveScreen';
import BottomSheet from '../../../commons/BottomSheet';
// Matches backend BabySeatType enum: InfantSeat=0, ChildSeat=1, BoosterSeat=2
const SEAT_CONFIG = {
    0: { labelKey: 'baby_seat_infant_seat', descKey: 'baby_seat_infant_desc' },
    1: { labelKey: 'baby_seat_child_seat', descKey: 'baby_seat_child_desc' },
    2: { labelKey: 'baby_seat_booster_seat', descKey: 'baby_seat_booster_desc' },
};
const ABSOLUTE_MAX_SEATS = 3;
const BabySeatSelector = ({ babySeatOptions, infantSeatCount, childSeatCount, boosterSeatCount, onInfantSeatChange, onChildSeatChange, onBoosterSeatChange, currencyLabel, usdCurrencyLabel, currency, disabled = false, readOnly = false, isPendingQuotation = false, isQuoting = false, isRateTypeAnimating = false, quotedOptions = null, loadingSeatTypes, onQuantityChange, t, }) => {
    const [openTooltip, setOpenTooltip] = useState(null);
    const anchorRefs = useRef({});
    // Refs to each rendered seat item, keyed by BabySeatType. Used to sync the
    // pulse animation phase of newly-loading items with already-pulsing
    // elements (e.g. the "to pay now" block that started pulsing on a previous
    // click within the debounce window).
    const itemRefs = useRef({});
    // Track which seats were loading on the previous render so we can detect
    // the false -> true transition and only sync the animation phase on entry.
    const prevLoadingRef = useRef(new Set());
    const { isMobile } = useResponsiveScreen();
    // Keep live refs to the current seat counts so handleChange and onClick
    // handlers always read the latest values instead of closure-captured props.
    // When the user clicks +/- faster than React re-renders, the event handlers
    // attached in the previous render would otherwise see stale counts for the
    // other seat types and emit an incorrect (updatedInfant, updatedChild,
    // updatedBooster) tuple to onQuantityChange — which produced the observed
    // bug where the WS babyseat.quotation.request carried a selection that did
    // not match the UI (e.g. {1,1,0} sent while the UI showed {0,0,0}), the
    // backend returned taxes for those phantom seats, and those taxes remained
    // visible (and even persisted across refresh via the cached quotation).
    const infantSeatCountRef = useRef(infantSeatCount);
    const childSeatCountRef = useRef(childSeatCount);
    const boosterSeatCountRef = useRef(boosterSeatCount);
    useEffect(() => { infantSeatCountRef.current = infantSeatCount; }, [infantSeatCount]);
    useEffect(() => { childSeatCountRef.current = childSeatCount; }, [childSeatCount]);
    useEffect(() => { boosterSeatCountRef.current = boosterSeatCount; }, [boosterSeatCount]);
    // Sync pulse animation phase with elements already pulsing elsewhere
    // (typically the "to pay now" / "pay at destination" blocks that entered
    // loading on the same user click). Without this, a seat that joins the
    // loading set mid-flight (second click during the 2-second debounce) would
    // start its animation at 0% while the payment block is mid-cycle, producing
    // a visible out-of-phase flicker.
    useEffect(() => {
        const currentLoading = loadingSeatTypes !== null && loadingSeatTypes !== void 0 ? loadingSeatTypes : ((isQuoting || isRateTypeAnimating) ? new Set([0, 1, 2]) : new Set());
        const prevLoading = prevLoadingRef.current;
        // Find any element that is already pulsing (not one that is joining now)
        // so we can read its current animation phase.
        const findReferenceAnimation = () => {
            var _a, _b, _c;
            const candidates = document.querySelectorAll('.transaction__block--loading, .baby-seat__item--loading');
            for (const candidate of Array.from(candidates)) {
                // Skip elements that are entering loading in this same commit
                const type = Number((_a = candidate.getAttribute('data-baby-seat-type')) !== null && _a !== void 0 ? _a : NaN);
                if (!Number.isNaN(type) && currentLoading.has(type) && !prevLoading.has(type))
                    continue;
                const anims = (_c = (_b = candidate.getAnimations) === null || _b === void 0 ? void 0 : _b.call(candidate)) !== null && _c !== void 0 ? _c : [];
                if (anims.length > 0)
                    return anims[0];
            }
            return null;
        };
        currentLoading.forEach((type) => {
            var _a, _b;
            if (prevLoading.has(type))
                return; // already pulsing, keep its phase
            const el = itemRefs.current[type];
            if (!el)
                return;
            const reference = findReferenceAnimation();
            if (!reference || reference.currentTime == null)
                return;
            const anims = (_b = (_a = el.getAnimations) === null || _a === void 0 ? void 0 : _a.call(el)) !== null && _b !== void 0 ? _b : [];
            if (anims.length === 0)
                return;
            anims[0].currentTime = reference.currentTime;
        });
        prevLoadingRef.current = new Set(currentLoading);
    }, [loadingSeatTypes, isQuoting, isRateTypeAnimating]);
    // Merge quoted options into the original list so that all seat types remain visible.
    // Quoted results from Momento 2 may only include seat types with quantity > 0,
    // so we must keep unquoted seat types from babySeatOptions (Momento 1 data).
    const effectiveOptions = React.useMemo(() => {
        if (!quotedOptions || quotedOptions.length === 0)
            return babySeatOptions;
        const byType = new Map();
        babySeatOptions.forEach((option) => {
            byType.set(option.babySeatType, option);
        });
        quotedOptions.forEach((option) => {
            const existing = byType.get(option.babySeatType);
            byType.set(option.babySeatType, {
                ...option,
                description: option.description || (existing === null || existing === void 0 ? void 0 : existing.description) || '',
            });
        });
        return Array.from(byType.values()).sort((a, b) => a.babySeatType - b.babySeatType);
    }, [babySeatOptions, quotedOptions]);
    // If no options at all, hide the entire section (AC#4)
    if (!effectiveOptions || effectiveOptions.length === 0) {
        return null;
    }
    const infantSeatOption = effectiveOptions.find(o => o.babySeatType === 0);
    const childSeatOption = effectiveOptions.find(o => o.babySeatType === 1);
    const boosterSeatOption = effectiveOptions.find(o => o.babySeatType === 2);
    const overallMax = Math.min(Math.max(...(effectiveOptions.map(o => o.maxQuantity).filter(q => q > 0)), 0), ABSOLUTE_MAX_SEATS);
    const totalOtherCount = (type) => {
        let total = 0;
        if (type !== 0)
            total += infantSeatCount;
        if (type !== 1)
            total += childSeatCount;
        if (type !== 2)
            total += boosterSeatCount;
        return total;
    };
    const infantSeatEffectiveMax = infantSeatOption
        ? Math.min(infantSeatOption.maxQuantity, overallMax - totalOtherCount(0))
        : 0;
    const childSeatEffectiveMax = childSeatOption
        ? Math.min(childSeatOption.maxQuantity, overallMax - totalOtherCount(1))
        : 0;
    const boosterSeatEffectiveMax = boosterSeatOption
        ? Math.min(boosterSeatOption.maxQuantity, overallMax - totalOtherCount(2))
        : 0;
    // Handler that triggers debounced quotation after local state update.
    // Reads the latest counts from refs (not stale closure props) so rapid
    // clicks that fire before React re-renders still emit a correct selection
    // tuple to onQuantityChange, and updates the refs synchronously so the
    // next click in the same event batch observes the new value (useEffect
    // only syncs refs after the next render).
    const handleChange = (type, newCount) => {
        const latestInfant = infantSeatCountRef.current;
        const latestChild = childSeatCountRef.current;
        const latestBooster = boosterSeatCountRef.current;
        const updatedInfant = type === 0 ? newCount : latestInfant;
        const updatedChild = type === 1 ? newCount : latestChild;
        const updatedBooster = type === 2 ? newCount : latestBooster;
        // No-op guard: avoid emitting a request when nothing actually changed
        // (e.g. a burst of identical clicks against the same rendered handler).
        if (updatedInfant === latestInfant && updatedChild === latestChild && updatedBooster === latestBooster) {
            return;
        }
        if (type === 0)
            infantSeatCountRef.current = newCount;
        if (type === 1)
            childSeatCountRef.current = newCount;
        if (type === 2)
            boosterSeatCountRef.current = newCount;
        if (type === 0)
            onInfantSeatChange(newCount);
        if (type === 1)
            onChildSeatChange(newCount);
        if (type === 2)
            onBoosterSeatChange(newCount);
        // Trigger the debounced quotation request
        if (onQuantityChange) {
            onQuantityChange(updatedInfant, updatedChild, updatedBooster);
        }
    };
    // Increment/decrement wrappers that read the live count + max from refs
    // instead of relying on the closure-captured button props. Without this,
    // three rapid +Infant clicks using the same rendered handler would all
    // compute newCount = 0+1 = 1 and collapse into a single change, masking
    // the fact that the backend should have received a request for count=3.
    const getSeatMax = (type) => {
        var _a, _b, _c;
        if (type === 0)
            return (_a = infantSeatOption === null || infantSeatOption === void 0 ? void 0 : infantSeatOption.maxQuantity) !== null && _a !== void 0 ? _a : 0;
        if (type === 1)
            return (_b = childSeatOption === null || childSeatOption === void 0 ? void 0 : childSeatOption.maxQuantity) !== null && _b !== void 0 ? _b : 0;
        return (_c = boosterSeatOption === null || boosterSeatOption === void 0 ? void 0 : boosterSeatOption.maxQuantity) !== null && _c !== void 0 ? _c : 0;
    };
    const getLatestCount = (type) => {
        if (type === 0)
            return infantSeatCountRef.current;
        if (type === 1)
            return childSeatCountRef.current;
        return boosterSeatCountRef.current;
    };
    const getLatestEffectiveMax = (type) => {
        let totalOther = 0;
        if (type !== 0)
            totalOther += infantSeatCountRef.current;
        if (type !== 1)
            totalOther += childSeatCountRef.current;
        if (type !== 2)
            totalOther += boosterSeatCountRef.current;
        return Math.min(getSeatMax(type), overallMax - totalOther);
    };
    const handleIncrement = (type) => {
        const current = getLatestCount(type);
        const max = getLatestEffectiveMax(type);
        const next = Math.min(max, current + 1);
        if (next === current)
            return;
        handleChange(type, next);
    };
    const handleDecrement = (type) => {
        const current = getLatestCount(type);
        const next = Math.max(0, current - 1);
        if (next === current)
            return;
        handleChange(type, next);
    };
    const renderSeatItem = (option, count, effectiveMax, seatType) => {
        var _a;
        const config = SEAT_CONFIG[option.babySeatType];
        if (!config)
            return null;
        // When loadingSeatTypes is provided, blink only the seats the user modified.
        // Fallback to the global isQuoting / isRateTypeAnimating flags for the rate-type
        // toggle case — isRateTypeAnimating is the only one that survives WS listener
        // resets, so the pulse keeps running for the full 400 ms after a language change.
        const isSeatLoading = loadingSeatTypes
            ? loadingSeatTypes.has(option.babySeatType)
            : (isQuoting || isRateTypeAnimating);
        const isLoading = isSeatLoading || isRateTypeAnimating;
        const isPriceHidden = loadingSeatTypes
            ? isSeatLoading
            : (isPendingQuotation || isQuoting || isRateTypeAnimating);
        const isAvailableOnly = option.availableOnly === true;
        return (_jsxs("div", { className: `baby-seat__item${isLoading ? ' baby-seat__item--loading' : ''}`, "data-baby-seat-type": option.babySeatType, ref: (el) => { itemRefs.current[option.babySeatType] = el; }, children: [_jsxs("div", { className: "baby-seat__row baby-seat__row--top", children: [_jsxs("div", { className: "baby-seat__label-group", children: [_jsx("span", { className: "baby-seat__item-label", children: t(config.labelKey) }), _jsx("span", { className: "baby-seat__info-icon", role: "button", tabIndex: 0, ref: (el) => { anchorRefs.current[option.babySeatType] = el; }, "aria-label": option.description || t('baby_seat_tooltip'), onClick: () => setOpenTooltip(prev => prev === option.babySeatType ? null : option.babySeatType), onKeyDown: (e) => { if (e.key === 'Enter' || e.key === ' ') {
                                        e.preventDefault();
                                        setOpenTooltip(prev => prev === option.babySeatType ? null : option.babySeatType);
                                    } }, onMouseEnter: isMobile ? undefined : () => setOpenTooltip(option.babySeatType), onMouseLeave: isMobile ? undefined : () => setOpenTooltip(prev => prev === option.babySeatType ? null : prev), onFocus: isMobile ? undefined : () => setOpenTooltip(option.babySeatType), onBlur: isMobile ? undefined : () => setOpenTooltip(prev => prev === option.babySeatType ? null : prev), children: _jsxs("svg", { width: "12", height: "12", viewBox: "0 0 14 14", fill: "none", xmlns: "http://www.w3.org/2000/svg", children: [_jsx("circle", { cx: "7", cy: "7", r: "6.5", stroke: "currentColor" }), _jsx("text", { x: "7", y: "10", textAnchor: "middle", fontSize: "9", fill: "currentColor", fontWeight: "500", children: "?" })] }) }), isMobile ? (_jsx(BottomSheet, { isVisible: openTooltip === option.babySeatType, onClose: () => setOpenTooltip(null), children: _jsxs("div", { className: "baby-seat__tooltip-content", children: [_jsx("p", { className: "strong", children: t(config.labelKey) }), _jsx("p", { children: option.description || t('baby_seat_tooltip') })] }) })) : (_jsx(PopoverComponent, { isVisible: openTooltip === option.babySeatType, onClose: () => setOpenTooltip(null), anchorEl: (_a = anchorRefs.current[option.babySeatType]) !== null && _a !== void 0 ? _a : null, children: _jsxs("div", { className: "inclusion-message-content", children: [_jsx("p", { className: "strong", children: t(config.labelKey) }), _jsx("p", { children: option.description || t('baby_seat_tooltip') })] }) }))] }), _jsx("span", { className: "baby-seat__item-price", children: isPriceHidden ? (_jsx("span", { className: "baby-seat__item-price--loading", children: formatCurrencyWithSymbol(option.paymentType === 1 ? usdCurrencyLabel : currencyLabel, option.pricePerUnit.toFixed(2)) })) : isAvailableOnly ? (_jsx("span", { className: "baby-seat__item-price--placeholder", children: formatCurrencyWithSymbol(option.paymentType === 1 ? usdCurrencyLabel : currencyLabel, option.pricePerUnit.toFixed(2)) })) : (formatCurrencyWithSymbol(option.paymentType === 1 ? usdCurrencyLabel : currencyLabel, option.pricePerUnit.toFixed(2))) })] }), _jsxs("div", { className: "baby-seat__row baby-seat__row--bottom", children: [_jsx("span", { className: "baby-seat__item-desc", children: t(config.descKey) }), readOnly ? (_jsx("span", { className: "baby-seat__qty baby-seat__qty--readonly", children: _jsxs("span", { className: "baby-seat__qty-value baby-seat__qty-value--active", children: ["x", count] }) })) : (_jsxs("div", { className: `baby-seat__qty${isLoading ? ' baby-seat__qty--disabled' : ''}`, children: [_jsx("button", { type: "button", className: "baby-seat__qty-btn", onClick: () => handleDecrement(seatType), disabled: disabled || count === 0, "aria-label": t('baby_seat_decrease', { seat: t(config.labelKey) }), children: "\u2212" }), _jsx("span", { className: `baby-seat__qty-value${count > 0 ? ' baby-seat__qty-value--active' : ''}`, children: count }), _jsx("button", { type: "button", className: "baby-seat__qty-btn", onClick: () => handleIncrement(seatType), disabled: disabled || count >= effectiveMax, "aria-label": t('baby_seat_increase', { seat: t(config.labelKey) }), children: "+" })] }))] })] }, option.babySeatType));
    };
    const hasSelection = infantSeatCount > 0 || childSeatCount > 0 || boosterSeatCount > 0;
    // In read-only mode, hide the section entirely when no seat was added and
    // render only the seat types the user actually added (count > 0).
    if (readOnly && !hasSelection) {
        return null;
    }
    // Only render seat types that are available (AC#3 -- hide unavailable types).
    // In read-only mode, additionally hide types with count === 0.
    const items = [
        infantSeatOption && (!readOnly || infantSeatCount > 0) && renderSeatItem(infantSeatOption, infantSeatCount, infantSeatEffectiveMax, 0),
        childSeatOption && (!readOnly || childSeatCount > 0) && renderSeatItem(childSeatOption, childSeatCount, childSeatEffectiveMax, 1),
        boosterSeatOption && (!readOnly || boosterSeatCount > 0) && renderSeatItem(boosterSeatOption, boosterSeatCount, boosterSeatEffectiveMax, 2),
    ].filter(Boolean);
    return (_jsxs(_Fragment, { children: [_jsx("div", { className: "baby-seat__options", children: items.map((item, idx) => (_jsxs(React.Fragment, { children: [idx > 0 && _jsx("div", { className: "baby-seat__divider" }), item] }, idx))) }), hasSelection && !isPendingQuotation && (_jsx(BabySeatPriceBreakdown, { babySeatOptions: effectiveOptions, infantSeatCount: infantSeatCount, childSeatCount: childSeatCount, boosterSeatCount: boosterSeatCount, currencyLabel: currencyLabel, usdCurrencyLabel: usdCurrencyLabel, isQuoting: isQuoting, t: t }))] }));
};
export default BabySeatSelector;
